import { supabase } from './supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { debug } from './logger';

export interface PhonePhotoEvent {
  event: 'photo:add' | 'photo:remove' | 'session:sync';
  payload: {
    index?: number;
    url?: string;
    photos?: { index: number; url: string }[];
  };
}

/**
 * Create a new photo capture session.
 * Returns the session token UUID.
 * Performs lazy cleanup of expired sessions and orphaned temp files.
 */
export async function createPhotoSession(): Promise<string> {
  await cleanupStaleSessions();

  const { data, error } = await supabase
    .from('photo_capture_sessions')
    .insert({})
    .select('token')
    .single();

  if (error) throw new Error(`Failed to create session: ${error.message}`);
  return data.token;
}

/**
 * Check if a session token is valid (exists and not expired).
 */
export async function isSessionValid(token: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('photo_capture_sessions')
    .select('token')
    .eq('token', token)
    .gt('expires_at', new Date().toISOString())
    .maybeSingle();

  return !error && !!data;
}

/**
 * Create a Realtime channel for a photo capture session.
 * Registers broadcast handlers but does NOT subscribe — caller must subscribe
 * after adding presence/other callbacks.
 * Returns the channel (caller must subscribe and later unsubscribe).
 */
export function subscribeToSession(
  token: string,
  onEvent: (event: PhonePhotoEvent) => void
): RealtimeChannel {
  const channel = supabase.channel(`photo-session:${token}`, {
    config: { broadcast: { self: true } },
  });

  channel.on('broadcast', { event: 'photo:add' }, (payload) => {
    debug('[photo-session] Received broadcast photo:add', payload);
    onEvent({ event: 'photo:add', payload: payload.payload as { index: number; url: string } });
  });

  channel.on('broadcast', { event: 'photo:remove' }, (payload) => {
    debug('[photo-session] Received broadcast photo:remove', payload);
    onEvent({ event: 'photo:remove', payload: payload.payload as { index: number } });
  });

  channel.on('broadcast', { event: 'session:sync' }, (payload) => {
    debug('[photo-session] Received broadcast session:sync', payload);
    onEvent({
      event: 'session:sync',
      payload: payload.payload as { photos: { index: number; url: string }[] },
    });
  });

  return channel;
}

/**
 * Delete a photo capture session row.
 */
export async function deletePhotoSession(token: string): Promise<void> {
  await supabase.from('photo_capture_sessions').delete().eq('token', token);
}

/**
 * Delete expired sessions and orphaned temp files from storage.
 */
export async function cleanupStaleSessions(): Promise<void> {
  await supabase
    .from('photo_capture_sessions')
    .delete()
    .lt('expires_at', new Date().toISOString());

  const { data: folders } = await supabase.storage.from('temp-photos').list('', {
    limit: 100,
    sortBy: { column: 'name', order: 'asc' },
  });

  if (!folders) return;

  for (const folder of folders) {
    if (!folder.id) continue;
    const token = folder.name;
    const valid = await isSessionValid(token);
    if (!valid) {
      const { data: files } = await supabase.storage.from('temp-photos').list(token);
      if (files && files.length > 0) {
        const paths = files.map((f) => `${token}/${f.name}`);
        await supabase.storage.from('temp-photos').remove(paths);
      }
    }
  }
}